import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid2,
  Box,
  Typography,
  Paper,
  IconButton,
  Button,
  TextField,
  Alert, Snackbar,
  Tooltip
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { getContentTypes, deleteContent } from "../../actions/contentActions";
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link, useNavigate } from "react-router-dom";
import AddButton from "../../themes/overrides/AddButton";
import SearchField from "../../themes/overrides/SearchField";
import ContentPopup from "./ContentPopup";
import EditIcon from "../../components/icons/EditIcon";
import RemoveIcon from "../../components/icons/RemoveIcon";
import WorkspaceIcon from "../../assets/images/workspace_black.svg"
import MediaIcon from "../../assets/images/brush.svg"
import LoadingOverlay from "../../components/LoadingOverlay";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import { usePermission } from "../authentication/permission-providor/UserPermission";
import CopyIcon from "../../components/icons/CopyIcon";

import { useParams } from 'react-router-dom';

const ContentModel = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const contentTypes = useSelector((state) => state.form.contentTypes);
  const [loading, setLoading] = useState(() => {
    return !contentTypes || contentTypes.length === 0;
  });
  const [shouldRefresh, setShouldRefresh] = useState(true);
  const [searchTerm, setSearchTerm] = useState(""); // State for search input
  const [modalOpen, setModalOpen] = useState(false);
  const theme = useTheme();

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [deleteOpenModal, setDeleteOpenModal] = useState(false);
  const [contentModelToDelete, setContentModelToDelete] = useState(false)
  const { userRole, hasPermission } = usePermission();

  const { workspaceId } = useParams();

  useEffect(() => {
    if (shouldRefresh && workspaceId) {
      setLoading(true);
      dispatch(getContentTypes(workspaceId))
        .finally(() => setLoading(false));
      setShouldRefresh(false);
    } else if (!workspaceId) {
      setLoading(false);
    }
  }, [workspaceId, shouldRefresh, dispatch]);

  const handleEdit = (id, form) => {
    navigate(`/${workspaceId}/content-type/${id}`);
  };

  const handleDeleteModal = (id) => {
    setContentModelToDelete(id);
    setDeleteOpenModal(true)
  };

  const handleCopy = async (id) => {
    try {
      const textToCopy = id;
      await navigator.clipboard.writeText(textToCopy);
      setSuccessMessage("Content type ID copied to clipboard!");
      setOpenSnackbar(true);
      setSeverity("success");

    } catch (err) {
      setSuccessMessage("Failed to copy");
      setOpenSnackbar(true);
      setSeverity("error");

    }
  }

  const handleDelete = () => {
    try {
      dispatch(deleteContent(contentModelToDelete));
      setSuccessMessage("Content model deleted successfully!");
      setOpenSnackbar(true);
      setSeverity("success");
    } catch (error) {
      setErrorMessage("Failed to delete content model. Please try again.");
      setOpenSnackbar(true);
      setSeverity("error");
    } finally {
      setDeleteOpenModal(false)
      setContentModelToDelete(null);
    }
  }

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setErrorMessage("");
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };


  const openPopup = () => {
    console.log('USER ROLE : ', userRole);

    setModalOpen(true);
  };

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "name", headerName: "Name", flex: 1 },
    { field: "slug", headerName: "Slug", flex: 1 },
    { field: "description", headerName: "Description", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <IconButton
            color="secondary"
            onClick={() => handleEdit(params.row._id, params)}
            aria-label="edit"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="secondary"
            onClick={() => handleDeleteModal(params.row._id)}
            aria-label="delete"
            disabled={!hasPermission('admin')}
          >
            <RemoveIcon />
          </IconButton>
          <Tooltip title="Copy Content Type ID" arrow>
            <IconButton
              color="secondary"
              onClick={() => handleCopy(params.row._id)}
              aria-label="copy"
            >
              <CopyIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  // Filter rows based on the search term
  const filteredRows = contentTypes
    .filter(
      (contentType) =>
        contentType.name.toLowerCase().includes(searchTerm) ||
        contentType.slug.toLowerCase().includes(searchTerm) ||
        contentType.description.toLowerCase().includes(searchTerm)
    )
    .map((contentType, index) => ({
      id: index + 1,
      ...contentType,
    }));


  const handlePopupSuccess = () => {
    setModalOpen(false);
    setShouldRefresh(true);
  }

  return (
    loading ? <LoadingOverlay /> :
      <Grid2 container spacing={2}>
        <Grid2 size={{ xs: 12, md: 8 }} display='flex' alignItems="center" sx={{ marginLeft: { xs: 4, md: 0 } }}>
          <Box component="img" sx={{ width: { xs: 32, md: 40 }, marginRight: { xs: 2, md: 4 } }} src={WorkspaceIcon} />
          <Typography variant="h1">Content Model</Typography>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 4 }} display="flex" justifyContent="flex-end">
          <AddButton
            component={Link}
            // to={`/${workspaceId}/content-type`}
            onClick={openPopup}
            label="Create New Content Model"
            disabled={!hasPermission('editor')}
          />
        </Grid2>
        <Grid2 size={12} container>
          <Grid2 size={{ xs: 12, md: 4 }} sx={{ marginTop: { xs: 2 } }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexGrow: 1
              }}>

              {/* <Typography variant="p" sx={{  textAlign: "left" }}>
            Recent Content Models
            </Typography> */}

              <SearchField
                placeholder="Search Content Models"
                variant="outlined"
                fullWidth
                value={searchTerm}
                onChange={handleSearch}
                sx={{ flex: 1 }}
              />
            </Box>
          </Grid2>
          <Grid2 size={12}>
            <Paper elevation={2} sx={{ padding: 2, marginBottom: 2, marginTop: { md: 2 }, overflowX: 'auto' }}>

              <Box sx={{ width: '100%', minWidth: { xs: 700, md: '100%' }, minHeight: 100, alignContent: 'center' }}>
                {columns.length > 0 ?
                  <DataGrid
                    rows={filteredRows}
                    columns={columns}
                    disableColumnSelector
                    disableRowSelectionOnClick
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 15,
                        },
                      },
                    }}
                    pageSizeOptions={[15]}
                    disableColumnMenu
                    sx={{
                      '& .MuiDataGrid-cell:focus': {
                        outline: 'none'
                      },
                    }}
                  />
                  :
                  <Typography variant="body1" gutterBottom align="center">No Contents</Typography>
                }
              </Box>
            </Paper>
          </Grid2>
        </Grid2>
        <ContentPopup
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
          }}
          onSuccess={handlePopupSuccess}
        />

        <DeleteConfirmationModal
          open={deleteOpenModal}
          onClose={() => {
            setDeleteOpenModal(false);
            setContentModelToDelete(null);
          }}
          text="Are you sure you want to delete this content model? Deleting it will also remove all related contents."
          onConfirm={handleDelete}
          deleteButtonLabel="Delete"
        />

        <ContentPopup
          isOpen={modalOpen}
          onClose={() => {
            setModalOpen(false);
          }}
          onSuccess={handlePopupSuccess}
        />
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={severity}
            sx={{ width: "100%" }}
            variant="filled"
          >
            {severity === "success" ? successMessage : errorMessage}
          </Alert>
        </Snackbar>
      </Grid2>
  );
};

export default ContentModel;
